import {
  input,
  inject,
  signal,
  output,
  Directive,
  DestroyRef,
  HostListener,
  InputSignal,
  OutputEmitterRef,
  WritableSignal,
  Renderer2,
} from '@angular/core';

import { Tooltip } from 'bootstrap';

/**
 * core
 */
import { ClipboardService } from 'src/app/core/services/navigator/clipboard/clipboard.service';

/**
 * directives
 */
import { TooltipDirective } from '../tooltip/tooltip.directive';

/**
 * enums
 */
import { ClipboardFormat, IClipboardFormat } from './enums';

/**
 * interfaces
 */
import { IClipboardValue, IClipboardCopiedEvent } from './interfaces';

/**
 * @directive ClipboardDirective
 * @description Attaches clipboard copy behavior to any host element.
 * On click, copies the bound `appClipboard` value using {@link ClipboardService}.
 *
 * Supports: `string`, `number`, `Blob`, `File`, `HTMLImageElement`.
 * Format is detected automatically from the value type when `clipboardFormat="auto"` (default).
 *
 * After copying:
 * - Emits `(copied)` with the result details.
 * - Adds the CSS class `clipboard-copied` to the host for `clipboardDuration` ms.
 * - If `appTooltip` is on the same element, temporarily shows `clipboardLabel` in the tooltip.
 *
 * @example
 * <!-- text -->
 * <button [appClipboard]="'Hello'" (copied)="onCopied($event)">Copy</button>
 *
 * @example
 * <!-- with tooltip feedback -->
 * <button [appClipboard]="value" appTooltip="Click to copy" clipboardLabel="Copied!">Copy</button>
 *
 * @example
 * <!-- image element reference -->
 * <button [appClipboard]="imgRef" clipboardFormat="image">Copy Image</button>
 */
@Directive({
  selector: '[appClipboard]',
  host: { '[class.clipboard-copied]': '_isCopied()' }
})
export class ClipboardDirective {

  //--------------------------------------------------------------------------
  // Injections
  //--------------------------------------------------------------------------

  private readonly _renderer = inject(Renderer2);
  private readonly _clipboard = inject(ClipboardService);
  private readonly _tooltip   = inject(TooltipDirective, { optional: true });

  //--------------------------------------------------------------------------
  // Inputs
  //--------------------------------------------------------------------------

  /**
   * @input appClipboard
   * @description The value to copy to the clipboard on click.
   * Accepts `string`, `number`, `Blob`, `File`, or `HTMLImageElement`.
   */
  public readonly value: InputSignal<IClipboardValue> = input.required<IClipboardValue>({ alias: 'appClipboard' });

  /**
   * @input clipboardFormat
   * @description The clipboard format to use. Defaults to `'auto'` which detects
   * the format from the value type automatically.
   * @default ClipboardFormat.Auto
   */
  public readonly format: InputSignal<IClipboardFormat> = input<IClipboardFormat>(ClipboardFormat.Auto, {
    alias: 'clipboardFormat'
  });

  /**
   * @input clipboardLabel
   * @description Label shown in the tooltip (if present on the same host) after a
   * successful copy. Reverts to the original tooltip title after `clipboardDuration` ms.
   * @default 'Copied!'
   */
  public readonly successLabel: InputSignal<string> = input<string>('Copied!', {
    alias: 'clipboardLabel'
  });

  /**
   * @input clipboardDuration
   * @description Duration in ms the `clipboard-copied` CSS class stays on the host
   * and the tooltip shows the success label.
   * @default 2000
   */
  public readonly duration: InputSignal<number> = input<number>(2000, {
    alias: 'clipboardDuration'
  });

  //--------------------------------------------------------------------------
  // Outputs
  //--------------------------------------------------------------------------

  /**
   * @output copied
   * @description Emitted after every copy attempt with the outcome details.
   */
  public readonly copied: OutputEmitterRef<IClipboardCopiedEvent> = output<IClipboardCopiedEvent>({ alias: 'copied' });

  //--------------------------------------------------------------------------
  // State
  //--------------------------------------------------------------------------

  /**
   * @description Drives the `clipboard-copied` CSS host binding.
   * @internal
   */
  public readonly _isCopied: WritableSignal<boolean> = signal(false);

  private _timer?: ReturnType<typeof setTimeout>;
  private _originalTitle?: Tooltip.Options['title'];

  //--------------------------------------------------------------------------
  // Constructor
  //--------------------------------------------------------------------------

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this._timer));
  }

  //--------------------------------------------------------------------------
  // Host Events
  //--------------------------------------------------------------------------

  /**
   * @description Handles the host click event. Resolves the format, invokes the
   * matching {@link ClipboardService} method, then emits the result and
   * applies visual feedback.
   */
  @HostListener('click')
  public async onCopy(): Promise<void> {
    const value  = this.value();
    const format = this._resolveFormat(value);
    let result;

    if (format === 'image') {
      result = await this._clipboard.writeImage(value as Blob | File | HTMLImageElement);
    } else if (format === 'html') {
      result = await this._clipboard.writeHtml(String(value));
    } else {
      result = await this._clipboard.writeText(value as string | number);
    }

    this.copied.emit({ ...result, format, value });
    this._applyFeedback();
  }

  //--------------------------------------------------------------------------
  // Private
  //--------------------------------------------------------------------------

  /**
   * @description Resolves the effective copy format.
   * When `format` is `'auto'`, detects from the value type:
   * - `Blob | File | HTMLImageElement` → `'image'`
   * - `string` starting with `<` → `'html'`
   * - Everything else → `'text'`
   * @param value The current input value.
   */
  private _resolveFormat(value: IClipboardValue): 'text' | 'html' | 'image' {
    const fmt = this.format();
    if (fmt !== ClipboardFormat.Auto) return fmt as 'text' | 'html' | 'image';
    if (value instanceof Blob || value instanceof File || value instanceof HTMLImageElement) return 'image';
    if (typeof value === 'string' && value.trimStart().startsWith('<')) return 'html';
    return 'text';
  }

  /**
   * @description Sets `_isCopied` and optionally updates the sibling tooltip label.
   * Schedules a reset after `duration` ms.
   */
  private _applyFeedback(): void {

    if (this._timer != null) {
      clearTimeout(this._timer);
    }

    this._isCopied.set(true);

    if (this._tooltip) {
      this._originalTitle = this._tooltip.title;
      this._tooltip.setContent({ '.tooltip-inner': this.successLabel() });
      this._renderer.addClass(this._tooltip!.tipElement, 'tooltip-success');
    }

    this._timer = setTimeout(() => this._resetFeedback(), this.duration());
    
  }

  /**
   * @description Resets `_isCopied` and restores the original tooltip title.
   */
  private _resetFeedback(): void {
    
    this._isCopied.set(false);

    if (this._tooltip) {
      this._tooltip.hide();
      this._renderer.removeClass(this._tooltip.tipElement, 'tooltip-success')
      if (this._originalTitle != null) {
        this._tooltip.setContent({ '.tooltip-inner': this._originalTitle as string });
      }
    }
  }

}
